import type { StateCreator } from 'zustand';
import type { StoreType } from '@/types/store';
import type { PersistedState } from '@/types/persistence';
import { STORAGE_KEY, STORAGE_VERSION, DEFAULT_SETTINGS } from '@/types/persistence';
import type { StorageAdapter as AppLockStorageAdapter } from '@ascend/applock';
import { localStorageAdapter, deserializeState } from '@/persistence/localStorageAdapter';
import { checkQuota } from '@/persistence/quotaGuard';
import { createDefaultStudy } from '@/data/factory';
import {
  registerPersistControls,
  savePersistedState,
} from '@/persistence/lockStorageBridge';

export function createPersistMiddleware<T extends StoreType>(
  storeInitializer: StateCreator<T, [], []>,
): StateCreator<T, [], []> {
  return (set, get, api) => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    let savesEnabled = false;

    const wrappedSet: typeof set = (partial, replace) => {
      (set as (partial: unknown, replace?: boolean) => void)(partial, replace);
      if (savesEnabled) schedulePersist(get as () => StoreType);
    };

    function schedulePersist(getState: () => StoreType) {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        try {
          const state = getState();
          const persisted: PersistedState = {
            version: STORAGE_VERSION,
            activeStudyId: state.activeStudyId,
            studies: state.studies,
            settings: state.settings,
            ...(state.dataset ? { dataset: state.dataset } : {}),
            auditTrail: state.trail,
          };
          const guard = checkQuota(persisted);
          if (guard.atLimit) {
            state.pushToast?.('Storage limit reached — export to CSV to free space.', 'danger');
            return;
          }
          void savePersistedState(persisted).catch((err) => {
            console.warn('persist save failed', err);
          });
          if (guard.nearLimit) {
            state.pushToast?.('Approaching storage limit. Export to CSV to free space.', 'warn');
          }
        } catch (err) {
          console.warn('persist failed', err);
        }
      }, 500);
    }

    async function rehydrateFromLockStore(
      lockStore: AppLockStorageAdapter,
    ): Promise<void> {
      try {
        const data = await lockStore.load<PersistedState>(STORAGE_KEY);
        if (data) {
          const state = get() as StoreType;
          set({
            studies: data.studies ?? state.studies,
            activeStudyId: data.activeStudyId ?? state.activeStudyId,
            settings: { ...state.settings, ...data.settings },
            ...(data.dataset ? { dataset: data.dataset } : {}),
            trail: data.auditTrail ?? state.trail,
          } as Partial<T>);
          queueMicrotask(() => {
            const s = get() as StoreType;
            if (typeof s.compute === 'function') s.compute();
            const active = s.studies.find((st) => st.id === s.activeStudyId);
            if (active && active.arms.some((a) => a.dataPoints.length > 0)) {
              s.pushToast?.('Previous session restored.', 'info');
            }
          });
        }
      } catch (err) {
        console.warn('rehydrate from lock store failed', err);
      }
    }

    registerPersistControls(
      () => {
        savesEnabled = true;
      },
      () => {
        savesEnabled = false;
        if (timer) {
          clearTimeout(timer);
          timer = null;
        }
      },
      async (store) => {
        await rehydrateFromLockStore(store);
        savesEnabled = true;
      },
    );

    try {
      const raw = localStorageAdapter.load();
      const defaultStudy = createDefaultStudy();
      const defaults: PersistedState = {
        version: STORAGE_VERSION,
        activeStudyId: defaultStudy.id,
        studies: [defaultStudy],
        settings: { ...DEFAULT_SETTINGS },
        auditTrail: [],
      };
      const restored = deserializeState(raw, defaults);
      savesEnabled = true;
      queueMicrotask(() => {
        const state = get() as StoreType;
        set({
          studies: restored.studies,
          activeStudyId: restored.activeStudyId,
          settings: { ...state.settings, ...restored.settings },
          ...(restored.dataset ? { dataset: restored.dataset } : {}),
          trail: restored.auditTrail,
        } as Partial<T>);
        queueMicrotask(() => {
          const s = get() as StoreType;
          if (typeof s.compute === 'function') s.compute();
          const active = s.studies.find((st) => st.id === s.activeStudyId);
          if (active && active.arms.some((a) => a.dataPoints.length > 0)) {
            s.pushToast?.('Previous session restored.', 'info');
          }
        });
      });
    } catch (err) {
      console.warn('persist hydration failed', err);
      savesEnabled = true;
    }

    return storeInitializer(wrappedSet as typeof set, get, api);
  };
}
